<script setup lang="ts">
/**
 * №18c (Игорь) — общий расход организации на ИИ в шапке, слева от переключателя темы.
 * «ИИ сегодня: X ₽ / Y ₽». Источник — costSummary() (ФАКТИЧЕСКИЙ метринг costed-провайдера,
 * не оценка → не завышено, закрывает 18b). По умолчанию период «сегодня» (решение Игоря).
 * Обновляется раз в минуту. При ошибке/недоступности (нет права/бюджета) — просто скрыт.
 *
 * Разбивка по фичам/периодам в поповере (полное 18c) требует агрегата ai_usage by feature
 * на бэкенде — отдельный шаг; здесь пока сводный «пульс расхода».
 * Тема-безопасно: surface-* классы адаптируются к светлой и тёмной.
 */
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { Sparkles } from "lucide-vue-next";
import { aiAssistantApi, type AiCostSummary } from "../../api/ai-assistant";
import { formatKopecks } from "../../utils/number-format";
import { useAuthStore } from "../../stores/auth";

const { t } = useI18n();
const auth = useAuthStore();
const cost = ref<AiCostSummary | null>(null);
let timer: ReturnType<typeof setInterval> | null = null;
async function load(): Promise<void> {
  // Без сессии не дёргаем costSummary: на публичных bare-страницах (опрос/преференсы)
  // виджет мог кратко смонтироваться на app-init (гонка роутера) и словить фоновый 401.
  if (!auth.isAuthenticated) { cost.value = null; return; }
  try { cost.value = await aiAssistantApi.costSummary(); } catch { cost.value = null; }
}
const spentLabel = computed(() => (cost.value ? formatKopecks(cost.value.spent_kopecks) : ""));
const budgetLabel = computed(() =>
  cost.value ? (cost.value.budget_kopecks == null ? t("ai_cost.no_limit") : formatKopecks(cost.value.budget_kopecks)) : "",
);
onMounted(() => { void load(); timer = setInterval(() => void load(), 60_000); });
onUnmounted(() => { if (timer) clearInterval(timer); });
</script>

<template>
  <span
    v-if="cost"
    class="hidden sm:inline-flex items-center gap-1 rounded-lg border border-surface-200 bg-surface-50 px-2.5 py-1 text-xs font-medium"
    :title="t('ai_cost.header_tooltip', { spent: spentLabel, budget: budgetLabel })"
    data-test="ai-spend-widget"
  >
    <Sparkles class="size-3.5 text-accent-500" />
    <span class="text-surface-400">{{ t("ai_cost.today") }}:</span>
    <span class="text-surface-800 tabular-nums">{{ spentLabel }}</span>
    <span class="text-surface-400">/ {{ budgetLabel }}</span>
  </span>
</template>
